import { selectUseBrowserTimeZone, selectUserTimeZone } from '@/features/auth/authSelector';
import {
  dayjsInTimezone,
  formatInTimezone,
  getBrowserTimezone,
  getEffectiveTimezone,
  getNextScheduledTime,
} from '@/utils/timezone';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

/**
 * Hook that provides timezone-aware date formatting functions
 * based on the user's timezone preferences
 */
const useTimezone = () => {
  const useBrowserTimeZone = useSelector(selectUseBrowserTimeZone);
  const userTimeZone = useSelector(selectUserTimeZone);

  // Get the effective timezone based on user settings
  const timezone = useMemo(() => {
    return getEffectiveTimezone(useBrowserTimeZone, userTimeZone);
  }, [useBrowserTimeZone, userTimeZone]);

  // Format a date in the user's timezone
  const formatDate = useCallback(
    (date, format = 'MMM DD, YYYY') => {
      if (!date) return '';
      return formatInTimezone(date, format, timezone);
    },
    [timezone]
  );

  // Format a date with time in the user's timezone
  const formatDateTime = useCallback(
    (date, format = 'MM/DD/YYYY HH:mm') => {
      if (!date) return '-';
      return formatInTimezone(date, format, timezone);
    },
    [timezone]
  );

  // Format a short date (for charts) in the user's timezone
  const formatShortDate = useCallback(
    (date, format = 'MMM DD') => {
      if (!date) return '';
      return formatInTimezone(date, format, timezone);
    },
    [timezone]
  );

  // Get a dayjs instance in the user's timezone
  const toTimezone = useCallback(
    (date) => {
      return dayjsInTimezone(date, timezone);
    },
    [timezone]
  );

  // Calculate next scheduled time based on scheduledTimes array
  const getNextAutoUpdate = useCallback(
    (scheduledTimes, format = 'MM/DD/YYYY, h:mm A') => {
      const nextTime = getNextScheduledTime(scheduledTimes, timezone);
      if (!nextTime) return '-';
      return nextTime.format(format);
    },
    [timezone]
  );

  return {
    timezone,
    browserTimezone: getBrowserTimezone(),
    useBrowserTimeZone,
    userTimeZone,
    formatDate,
    formatDateTime,
    formatShortDate,
    toTimezone,
    getNextAutoUpdate,
  };
};

export default useTimezone;
